import axios from "axios";
import config from "../config";

const BASE_URL = config.baseApi;

/**
 * Get admin token from localStorage
 * Tries admin_token first, then falls back to car-meet-storage.token
 * @returns {string|null} Token or null if not found
 */
const getAdminToken = () => {
  try {
    // First, try to get admin_token (as requested by user)
    let token = localStorage.getItem("admin_token");

    // If admin_token not found, try car-meet-storage (how the app actually stores it)
    if (!token) {
      const carMeetStorageString = localStorage.getItem("car-meet-storage");
      if (carMeetStorageString) {
        try {
          const carMeetStorage = JSON.parse(carMeetStorageString);
          token = carMeetStorage?.token || null;
          if (token) {
            console.log("ServiceFee: Using token from car-meet-storage");
          }
        } catch (e) {
          console.error("ServiceFee: Error parsing car-meet-storage:", e);
        }
      }
    } else {
      console.log("ServiceFee: Using token from admin_token");
    }

    if (!token) {
      console.warn("ServiceFee: No token found in localStorage (checked admin_token and car-meet-storage)");
    }

    return token;
  } catch (error) {
    console.error("Error getting admin token:", error);
    return null;
  }
};

/**
 * Service Fee Service
 * Handles service fee (commission) operations for tickets
 */
const ServiceFeeService = {
  /**
   * Get current service fee percentage
   * @returns {Promise} API response
   * @throws {Error} If error occurs (404 is expected when no fee exists)
   */
  async getServiceFee() {
    const token = getAdminToken();
    if (!token) {
      const error = new Error("Admin token not found in localStorage");
      error.isTokenError = true;
      throw error;
    }

    if (!BASE_URL) {
      const error = new Error("Base API URL is not configured");
      error.isConfigError = true;
      throw error;
    }

    try {
      const url = `${BASE_URL}/api/v1/admin/service-fee`;
      console.log("ServiceFee: Calling GET", url);

      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        timeout: 30000,
      });


      console.log("ServiceFee: Success response", response?.data);
      return response;

    } catch (error) {
      // Add helpful context to error
      console.error("ServiceFee: Error details", {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        url: `${BASE_URL}/api/v1/admin/service-fee`
      });

      if (!error.response) {
        // Network error or no response
        error.isNetworkError = true;
        error.message = error.message || "Network error. Please check your connection.";
      }
      // Re-throw to let caller handle 404 specifically
      throw error;
    }
  },

  /**
   * Create new service fee (use when GET returns 404)
   * @param {Object} feeData - Service fee data with percentage
   * @param {number} feeData.percentage - Commission percentage (0-100)
   * @returns {Promise} API response
   */
  async createServiceFee(feeData) {
    const token = getAdminToken();
    if (!token) throw new Error("Admin token not found");

    return axios.post(
      `${BASE_URL}/api/v1/admin/service-fee/create`,
      {
        percentage: feeData.percentage,
        adminPercentage: feeData.adminPercentage, // ✅ NEW
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    );
  },


  /**
   * Update existing service fee (use when fee already exists)
   * @param {Object} feeData - Service fee data with percentage
   * @param {number} feeData.percentage - Commission percentage (0-100)
   * @returns {Promise} API response
   */
  async updateServiceFee(feeData) {
    const token = getAdminToken();
    if (!token) throw new Error("Admin token not found");

    return axios.put(
      `${BASE_URL}/api/v1/admin/service-fee`,
      {
        percentage: feeData.percentage,
        adminPercentage: feeData.adminPercentage, // ✅ NEW
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    );
  },
};


export default ServiceFeeService;

